// src/components/ProjectsSection.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./ProjectsSection.css";

function useIsCoarsePointer() {
  const [coarse, setCoarse] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(pointer: coarse)");
    const update = () => setCoarse(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  return coarse;
}

export default function ProjectsSection({ items }) {
  const isMobileLike = useIsCoarsePointer();

  return (
    <section className="projects-section" id="projects">
      <div className="projects-header">
        <h2>Projects</h2>
        <p className="projects-subtitle">Selected works</p>
        <p className="projects-hint">
          {isMobileLike ? "Swipe to browse." : "Scroll to browse • Keep scrolling to continue."}
        </p>
      </div>

      {isMobileLike ? <ProjectsSnapRow items={items} /> : <ProjectsCoverflow items={items} />}
    </section>
  );
}

function ProjectCard({ item, priority = false }) {
  const demoHref = item.demo ? `/projects/${item.slug}?demo=1` : null;

  return (
    <article className="project-card">
      <div className="project-media">
        <img src={item.image} alt={item.title} loading={priority ? "eager" : "lazy"} />
      </div>

      <div className="project-body">
        <h3 className="project-title">{item.title}</h3>

        {item.awards ? <div className="project-awards">{item.awards}</div> : null}
        {item.description ? <p className="project-desc">{item.description}</p> : null}
        {item.tech ? <div className="project-tech">{item.tech}</div> : null}

        <div className="project-actions">
          {demoHref ? (
            <a className="project-cta" href={demoHref}>
              Demo
            </a>
          ) : (
            <span className="project-private">No Demo</span>
          )}

          {item.github ? (
            <a className="project-cta ghost" href={item.github} target="_blank" rel="noreferrer noopener">
              GitHub
            </a>
          ) : (
            <span className="project-private">Private</span>
          )}

          {item.live ? (
            <a className="project-cta ghost" href={item.live} target="_blank" rel="noreferrer noopener">
              Live
            </a>
          ) : null}
        </div>
      </div>
    </article>
  );
}


/** Mobile: smooth swipe with scroll-snap */
function ProjectsSnapRow({ items }) {
  const scrollerRef = useRef(null);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const onWheel = (e) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        const canScrollLeft = el.scrollLeft > 0;
        const canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
        const dir = e.deltaY > 0 ? 1 : -1;

        if ((dir < 0 && canScrollLeft) || (dir > 0 && canScrollRight)) {
          e.preventDefault();
          el.scrollLeft += e.deltaY;
        }
      }
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  return (
    <div className="projects-snap">
      <div className="projects-snap-row" ref={scrollerRef}>
        {items.map((item, idx) => (
          <div className="projects-snap-cell" key={idx}>
            <ProjectCard item={item} priority={idx < 2} />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Desktop: coverflow with click + arrows + wheel (no scroll trap at edges) */
function ProjectsCoverflow({ items }) {
  const [active, setActive] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const rootRef = useRef(null);
  const scrollTimeout = useRef(null);

  // wheel sensitivity controls
  const wheelAccumRef = useRef(0);
  const lastWheelTsRef = useRef(0);
  const lastTriggerTsRef = useRef(0);

  // Tune these:
  const WHEEL_THRESHOLD = 160; // higher = less sensitive (try 140~220)
  const COOLDOWN_MS = 220; // higher = slower repeated changes (try 180~300)

  // IMPORTANT: stable function so ESLint deps are satisfied
  const clampIndex = React.useCallback(
    (i) => Math.max(0, Math.min(items.length - 1, i)),
    [items.length]
  );

  // Keyboard nav (lint-clean)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") setActive((v) => clampIndex(v - 1));
      if (e.key === "ArrowRight") setActive((v) => clampIndex(v + 1));
    };

    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      clearTimeout(scrollTimeout.current);
    };
  }, [clampIndex]);

  // Wheel nav (lint-clean)
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    const onWheel = (e) => {
      const dy = e.deltaY;
      if (Math.abs(dy) < 2) return;

      const dir = dy > 0 ? 1 : -1;
      const atStart = active === 0;
      const atEnd = active === items.length - 1;

      // edge passthrough: let page scroll
      if ((dir < 0 && atStart) || (dir > 0 && atEnd)) {
        wheelAccumRef.current = 0;
        lastWheelTsRef.current = performance.now();
        return;
      }

      // consume wheel so page doesn't move
      e.preventDefault();

      // anti-accidental-click lock
      setIsScrolling(true);
      clearTimeout(scrollTimeout.current);
      scrollTimeout.current = setTimeout(() => setIsScrolling(false), 140);

      const now = performance.now();

      // decay if user paused
      if (now - lastWheelTsRef.current > 350) {
        wheelAccumRef.current = 0;
      }
      lastWheelTsRef.current = now;

      wheelAccumRef.current += dy;

      if (Math.abs(wheelAccumRef.current) < WHEEL_THRESHOLD) return;

      // cooldown between flips
      if (now - lastTriggerTsRef.current < COOLDOWN_MS) return;
      lastTriggerTsRef.current = now;

      // consume one "step"
      wheelAccumRef.current -= dir * WHEEL_THRESHOLD;

      setActive((v) => clampIndex(v + dir));
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [active, clampIndex, items.length]);

  const positions = useMemo(() => {
    const maxSide = 2;
    const arr = [];
    for (let i = 0; i < items.length; i++) {
      const d = i - active;
      if (Math.abs(d) <= maxSide) arr.push({ i, d });
    }
    return arr;
  }, [active, items.length]);

  return (
    <div className="coverflow" ref={rootRef}>
      <div className="coverflow-stage">
        {positions.map(({ i, d }) => {
          const x = d * 320;
          const scale = d === 0 ? 1.05 : 0.82;
          const rotate = d * -22;
          const z = d === 0 ? 90 : 0;

          return (
            <div
              key={i}
              className={`coverflow-item ${d === 0 ? "is-active" : ""}`}
              style={{
                transform: `
                  translateX(${x}px)
                  translateZ(${z}px)
                  rotateY(${rotate}deg)
                  scale(${scale})
                `,
                opacity: d === 0 ? 1 : 0.55,
                filter: d === 0 ? "none" : "blur(0.2px)",
                zIndex: 100 - Math.abs(d),
              }}
              onClick={() => {
                if (!isScrolling) setActive(i);
              }}
            >
              <ProjectCard item={items[i]} priority={d === 0} />
            </div>
          );
        })}
      </div>

      <div className="coverflow-controls">
        <button onClick={() => setActive((v) => clampIndex(v - 1))} aria-label="Previous project">
          ←
        </button>

        <div className="coverflow-dots">
          {items.map((_, i) => (
            <span
              key={i}
              className={`dot ${i === active ? "active" : ""}`}
              onClick={() => setActive(i)}
              title={`Project ${i + 1}`}
            />
          ))}
        </div>

        <button onClick={() => setActive((v) => clampIndex(v + 1))} aria-label="Next project">
          →
        </button>
      </div>
    </div>
  );
}
